import { useEffect, useState } from "react";
import { FileText, Upload, ExternalLink, Trash2, Power } from "lucide-react";
import { http, errMsg, fmtDate } from "../../api";

export default function CreatorAgreementsAdmin() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ kind: "nda", title: "Non-Disclosure Agreement", description: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = () => http.get("/admin/creator-agreements").then(r => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="loading">Loading agreements…</div>;

  const upload = async e => {
    e.preventDefault();
    if (!file) return setNotice({ tone: "error", text: "Choose a PDF file." });
    const fd = new FormData(); fd.append("kind", form.kind); fd.append("title", form.title); fd.append("description", form.description); fd.append("file", file);
    setBusy(true); setNotice(null);
    try { await http.post("/admin/creator-agreements", fd); setNotice({ tone: "success", text: "Agreement uploaded and active for shortlisted candidates." }); setFile(null); load(); }
    catch (err) { setNotice({ tone: "error", text: errMsg(err) }); } finally { setBusy(false); }
  };
  const openPdf = async id => { const { data: blob } = await http.get(`/admin/creator-agreements/${id}/file`, { responseType: "blob" }); window.open(URL.createObjectURL(blob), "_blank"); };
  const toggle = async id => { await http.patch(`/admin/creator-agreements/${id}`); load(); };
  const remove = async id => { if (!window.confirm("Delete this agreement template?")) return; try { await http.delete(`/admin/creator-agreements/${id}`); load(); } catch (err) { setNotice({ tone: "error", text: errMsg(err) }); } };
  const setKind = kind => setForm({ ...form, kind, title: kind === "nda" ? "Non-Disclosure Agreement" : kind === "freelancer" ? "Freelancer Agreement" : form.title });

  return (
    <>
      <form className="form-card" onSubmit={upload} data-testid="creator-agreement-upload">
        <h2>Upload agreement template</h2>
        <p>Shortlisted candidates must sign every active agreement. Upload one NDA and one Freelancer agreement (PDF). Uploading a new version of the same type replaces the active one; already-signed copies stay on record.</p>
        <div className="field-row three">
          <div><label>Type</label><select value={form.kind} onChange={e => setKind(e.target.value)} data-testid="agreement-kind">{Object.entries(data.kinds).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
          <div><label>Title</label><input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="agreement-title" /></div>
          <div><label>Short description (optional)</label><input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="e.g. Standard confidentiality terms" data-testid="agreement-description" /></div>
        </div>
        <label>PDF file (under 15 MB)</label>
        <input type="file" accept="application/pdf,.pdf" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="agreement-file" />
        {notice && <div className={`notice ${notice.tone}`} style={{ marginTop: 12 }} data-testid="agreement-notice">{notice.text}</div>}
        <button className="primary" disabled={busy} style={{ marginTop: 14 }} data-testid="agreement-upload-submit"><Upload size={15} /> {busy ? "Uploading…" : "Upload agreement"}</button>
      </form>
      <div className="list-rows" data-testid="creator-agreement-list">
        {data.items.length === 0 && <div className="empty">No agreement templates yet. Upload the NDA and Freelancer agreement above.</div>}
        {data.items.map(a => (
          <div key={a.id} className={`list-row ${a.active ? "" : "muted-row"}`} data-testid={`creator-agreement-${a.id}`}>
            <FileText size={18} color={a.active ? "var(--primary)" : "var(--muted)"} />
            <div className="list-row-info"><strong>{a.title}</strong><p>{a.kind_label} · {a.pages} page(s) · uploaded {fmtDate(a.created_at)} · signed by {a.signed_count} candidate(s){a.description ? ` · ${a.description}` : ""}</p></div>
            <div className="list-row-actions">
              <span className={`badge ${a.active ? "success" : ""}`}>{a.active ? "Active" : "Inactive"}</span>
              <button className="ghost" onClick={() => openPdf(a.id)} data-testid={`agreement-view-${a.id}`}><ExternalLink size={13} /> View</button>
              <button className="ghost" onClick={() => toggle(a.id)} data-testid={`agreement-toggle-${a.id}`}><Power size={13} /> {a.active ? "Deactivate" : "Activate"}</button>
              <button className="ghost danger-text" onClick={() => remove(a.id)} data-testid={`agreement-delete-${a.id}`}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
